import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { getOffer, deleteOffer, publishOffer } from '../../services/offers'
import { getUser } from '../../services/helpers'
import {
  TagIcon,
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  PhotoIcon,
  VideoCameraIcon,
  CheckCircleIcon,
  XCircleIcon,
  BuildingOfficeIcon,
  Bars3Icon,
  XMarkIcon,
  ChartBarIcon,
  PlusCircleIcon,
  ShoppingBagIcon,
  TicketIcon,
  WalletIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  ExclamationTriangleIcon,
  PlayIcon,
  ArrowsPointingOutIcon
} from '@heroicons/react/24/outline'

const CenterOfferDetailPage = () => {
  const { offerId } = useParams()
  const navigate = useNavigate()
  const [user, setUser] = useState(getUser())
  const displayName = user?.organization || user?.name || 'Event Center'
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [offer, setOffer] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [selectedMedia, setSelectedMedia] = useState(null)
  const [showMediaModal, setShowMediaModal] = useState(false)

  const tabs = [
    { id: 'overview', label: 'Overview', icon: ChartBarIcon, path: '/center' },
    { id: 'events', label: 'My Events', icon: CalendarIcon, path: '/center/events', disabled: true },
    { id: 'offers', label: 'My Offers', icon: TagIcon, path: '/center/offers' },
    { id: 'orders', label: 'Orders', icon: ShoppingBagIcon, path: '/center/orders' },
    { id: 'tickets', label: 'Tickets', icon: TicketIcon, path: '/center/tickets' },
    { id: 'media', label: 'Media Gallery', icon: PhotoIcon, path: '/center/media', disabled: true },
    { id: 'disputes', label: 'Disputes', icon: ExclamationTriangleIcon, path: '/center/disputes' },
    { id: 'wallet', label: 'Wallet', icon: WalletIcon, path: '/center/wallet' },
    { id: 'profile', label: 'Profile', icon: UserCircleIcon, path: '/center' }
  ]

  useEffect(() => {
    fetchOffer()
  }, [offerId])

  const fetchOffer = async () => {
    try {
      setLoading(true)
      setError('')
      const res = await getOffer(offerId)
      if (res.status === 200) {
        setOffer(res.data)
      } else {
        setError(res.error || 'Failed to load offer')
      }
      setLoading(false)
    } catch (err) {
      setError('Failed to load offer')
      setLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!offer) return

    // Check if any tickets have been sold
    if (offer.remaining < offer.quantity) {
      setError('Cannot delete offer with sold tickets. Please archive it instead.')
      setTimeout(() => setError(''), 5000)
      return
    }

    if (!window.confirm('Are you sure you want to delete this offer? This action cannot be undone.')) return

    setDeleting(true)
    setError('')
    try {
      const res = await deleteOffer(offerId)
      if (res.status === 200) {
        setSuccess('Offer deleted successfully')
        setTimeout(() => {
          navigate('/center/offers')
        }, 1500)
      } else {
        setError(res.error || 'Failed to delete offer')
        setTimeout(() => setError(''), 5000)
      }
    } catch (err) {
      setError('Failed to delete offer')
      setTimeout(() => setError(''), 5000)
    } finally {
      setDeleting(false)
    }
  }

  const handlePublish = async () => {
    setError('')
    try {
      const res = await publishOffer(offerId)
      if (res.status === 200) {
        setSuccess('Offer published successfully')
        fetchOffer()
        setTimeout(() => setSuccess(''), 3000)
      } else {
        setError(res.error || 'Failed to publish offer')
        setTimeout(() => setError(''), 5000)
      }
    } catch (err) {
      setError('Failed to publish offer')
      setTimeout(() => setError(''), 5000)
    }
  }

  const getStatusBadge = (status) => {
    const badges = {
      DRAFT: 'bg-gray-100 text-gray-700',
      ACTIVE: 'bg-green-100 text-green-700',
      INACTIVE: 'bg-yellow-100 text-yellow-700',
      EXPIRED: 'bg-red-100 text-red-700'
    }
    return badges[status] || 'bg-gray-100 text-gray-700'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading offer...</p>
        </div>
      </div>
    )
  }

  if (error && !offer) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <p className="text-lg font-semibold text-slate-900 mb-2">Offer not found</p>
          <p className="text-slate-600 mb-6">{error}</p>
          <Link
            to="/center/offers"
            className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-medium"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            Back to Offers
          </Link>
        </div>
      </div>
    )
  }

  if (!offer) return null

  const discount = offer.normalPrice > 0 
    ? Math.round(((offer.normalPrice - offer.discountedPrice) / offer.normalPrice) * 100) 
    : 0
  const soldCount = offer.quantity - offer.remaining
  const soldPercentage = (soldCount / offer.quantity) * 100
  const isExpired = offer.validUntil && new Date(offer.validUntil) < new Date()

  return (
    <div className="min-h-screen bg-slate-50 flex font-sans">
      {/* Sidebar Navigation */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-3 px-6 h-16 border-b border-slate-100">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-600 to-emerald-500 flex items-center justify-center text-white shadow-sm">
              <BuildingOfficeIcon className="w-5 h-5" />
            </div>
            <span className="text-xl font-bold text-slate-900 tracking-tight">Obule Panel</span>
            <button onClick={() => setMobileMenuOpen(false)} className="ml-auto lg:hidden">
              <XMarkIcon className="w-6 h-6 text-slate-400" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
            {tabs.filter(tab => !tab.disabled).map((tab) => {
              const Icon = tab.icon
              const isActive = tab.id === 'offers'
              
              return (
                <Link
                  key={tab.id}
                  to={tab.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                    isActive
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                >
                  <Icon className={`w-5 h-5 ${isActive ? 'text-emerald-600' : 'text-slate-400'}`} />
                  {tab.label}
                </Link>
              )
            })}
          </nav>

          {/* User Profile */}
          <div className="p-4 border-t border-slate-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-blue-500 flex items-center justify-center text-white font-bold">
                {displayName.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-900 truncate">{displayName}</p>
                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 lg:ml-64">
        <div className="p-6 lg:p-8 max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <Link
              to="/center/offers"
              className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4 transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5" />
              Back to Offers
            </Link>

            {/* Messages */}
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                {error}
              </div>
            )}
            {success && (
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
                {success}
              </div>
            )}

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-slate-900 mb-2">{offer.title}</h1>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusBadge(offer.status)}`}>
                    {offer.status}
                  </span>
                  {offer.category && (
                    <span className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-medium">
                      {offer.category}
                    </span>
                  )}
                  {discount > 0 && (
                    <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-bold">
                      {discount}% OFF
                    </span>
                  )}
                </div>
              </div>
              <div className="flex gap-3">
                <Link
                  to={`/center/offers/${offerId}/edit`}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-medium"
                >
                  <PencilIcon className="w-5 h-5" />
                  Edit
                </Link>
                {offer.status === 'DRAFT' && (
                  <button
                    onClick={handlePublish}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                  >
                    <CheckCircleIcon className="w-5 h-5" />
                    Publish
                  </button>
                )}
                <button
                  onClick={handleDelete}
                  disabled={deleting || offer.remaining < offer.quantity}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <TrashIcon className="w-5 h-5" />
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>

          {/* Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Images */}
              {offer.images && offer.images.length > 0 && (
                <div className="bg-white rounded-2xl border border-slate-200 p-6">
                  <h2 className="text-xl font-bold text-slate-900 mb-4">Images</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {offer.images.map((url, index) => (
                      <div
                        key={index}
                        className="relative aspect-square bg-slate-100 rounded-lg overflow-hidden cursor-pointer group"
                        onClick={() => {
                          setSelectedMedia({ type: 'IMAGE', url })
                          setShowMediaModal(true)
                        }}
                      >
                        <img
                          src={url}
                          alt={`${offer.title} ${index + 1}`}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          onError={(e) => {
                            e.target.style.display = 'none'
                          }}
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                          <ArrowsPointingOutIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Videos */}
              {offer.videos && offer.videos.length > 0 && (
                <div className="bg-white rounded-2xl border border-slate-200 p-6">
                  <h2 className="text-xl font-bold text-slate-900 mb-4">Videos</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {offer.videos.map((url, index) => (
                      <div
                        key={index}
                        className="relative aspect-video bg-slate-100 rounded-lg overflow-hidden cursor-pointer group"
                        onClick={() => {
                          setSelectedMedia({ type: 'VIDEO', url })
                          setShowMediaModal(true)
                        }}
                      >
                        <video
                          src={url}
                          className="w-full h-full object-cover"
                          controls={false}
                          onError={(e) => {
                            e.target.style.display = 'none'
                          }}
                        />
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                          <PlayIcon className="w-16 h-16 text-white" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Description */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <h2 className="text-xl font-bold text-slate-900 mb-4">Description</h2>
                <p className="text-slate-700 whitespace-pre-wrap">{offer.description}</p>
              </div>

              {/* Terms & Conditions */}
              {offer.terms && (
                <div className="bg-white rounded-2xl border border-slate-200 p-6">
                  <h2 className="text-xl font-bold text-slate-900 mb-4">Terms & Conditions</h2>
                  <p className="text-slate-700 whitespace-pre-wrap">{offer.terms}</p>
                </div>
              )}

              {/* Tags */}
              {offer.tags && offer.tags.length > 0 && (
                <div className="bg-white rounded-2xl border border-slate-200 p-6">
                  <h2 className="text-xl font-bold text-slate-900 mb-4">Tags</h2>
                  <div className="flex flex-wrap gap-2">
                    {offer.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm font-medium"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Pricing Card */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Pricing</h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Normal Price</p>
                    <p className="text-xl font-bold text-slate-700 line-through">
                      {offer.currency} {offer.normalPrice.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Discounted Price</p>
                    <p className="text-2xl font-bold text-emerald-600">
                      {offer.currency} {offer.discountedPrice.toLocaleString()}
                    </p>
                  </div>
                  {discount > 0 && (
                    <div className="pt-3 border-t border-slate-200">
                      <p className="text-sm font-semibold text-green-600">
                        You Save: {offer.currency} {(offer.normalPrice - offer.discountedPrice).toLocaleString()} ({discount}%)
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Availability Card */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Availability</h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm text-slate-600 mb-2">
                      <span>Sold: {soldCount} / {offer.quantity}</span>
                      <span>{offer.remaining} left</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-3">
                      <div
                        className="bg-emerald-600 h-3 rounded-full transition-all"
                        style={{ width: `${soldPercentage}%` }}
                      />
                    </div>
                  </div>
                  {offer.validFrom && (
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Valid From</p>
                      <p className="text-sm font-medium text-slate-900">
                        {new Date(offer.validFrom).toLocaleString()}
                      </p>
                    </div>
                  )}
                  {offer.validUntil && (
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Valid Until</p>
                      <p className={`text-sm font-medium ${isExpired ? 'text-red-600' : 'text-slate-900'}`}>
                        {new Date(offer.validUntil).toLocaleString()}
                      </p>
                      {isExpired && (
                        <p className="text-xs text-red-600 mt-1">This offer has expired</p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Quick Stats */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Quick Stats</h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-slate-500">Created</p>
                    <p className="text-sm font-medium text-slate-900">
                      {new Date(offer.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  {offer.updatedAt && offer.updatedAt !== offer.createdAt && (
                    <div>
                      <p className="text-xs text-slate-500">Last Updated</p>
                      <p className="text-sm font-medium text-slate-900">
                        {new Date(offer.updatedAt).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Mobile Menu Button */}
      <button
        onClick={() => setMobileMenuOpen(true)}
        className="fixed bottom-4 right-4 lg:hidden z-40 w-14 h-14 bg-emerald-600 text-white rounded-full shadow-lg flex items-center justify-center"
      >
        <Bars3Icon className="w-6 h-6" />
      </button>

      {/* Media Viewer Modal */}
      {showMediaModal && selectedMedia && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => { setShowMediaModal(false); setSelectedMedia(null); }}>
          <div className="relative max-w-7xl w-full h-full max-h-[90vh] flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            {/* Close Button */}
            <button
              onClick={() => { setShowMediaModal(false); setSelectedMedia(null); }}
              className="absolute top-4 right-4 w-12 h-12 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition-all z-50 backdrop-blur-sm"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>

            {/* Media Content */}
            <div className="w-full h-full flex items-center justify-center">
              {selectedMedia.type === 'IMAGE' ? (
                <img 
                  src={selectedMedia.url} 
                  alt="Media" 
                  className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                  onError={(e) => {
                    console.error('Failed to load image in modal:', selectedMedia.url)
                    e.target.style.display = 'none'
                  }}
                />
              ) : (
                <div className="w-full max-w-5xl">
                  <video 
                    src={selectedMedia.url} 
                    className="w-full h-full max-h-[80vh] rounded-lg shadow-2xl"
                    controls
                    autoPlay
                    onError={(e) => {
                      console.error('Failed to load video in modal:', selectedMedia.url)
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CenterOfferDetailPage

